import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Building2, DollarSign, Target, Clock, ChevronRight, Activity, CheckCircle, TrendingUp, FileText, RefreshCw } from 'lucide-react';

// Warm Premium Palette
const colors = {
  primaryTeal: '#3D6B67',
  primaryTealLight: '#4A7D78',
  primaryBrown: '#5C4033',
  primaryBrownDark: '#4A3328',
  bgCream: '#FAF6F2',
  bgSage: '#DCE7E3',
  bgCard: '#FFFFFF',
  textPrimary: '#2C1810',
  textSecondary: '#5D4E47',
  textTertiary: '#8B7D76',
  textOnDark: '#FFFFFF',
  borderWarm: '#E8E0D8',
  borderSage: '#C5D5CF',
  success: '#4D9078',
};

// Skeleton Components
const SkeletonPulse = ({ className = '', style = {} }) => (
  <div 
    className={`animate-pulse rounded-xl ${className}`}
    style={{ backgroundColor: colors.borderWarm, ...style }}
  />
);

const SkeletonStatCard = () => (
  <div 
    className="rounded-2xl p-6"
    style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderWarm}` }}
  >
    <SkeletonPulse className="w-10 h-10 mb-4" />
    <SkeletonPulse className="h-8 w-24 mb-2" />
    <SkeletonPulse className="h-4 w-20" />
  </div>
);

const SkeletonTableRow = () => (
  <tr style={{ borderBottom: `1px solid ${colors.borderWarm}` }}>
    <td className="px-6 py-4">
      <SkeletonPulse className="h-5 w-32 mb-2" />
      <SkeletonPulse className="h-3 w-20" />
    </td>
    <td className="px-6 py-4"><SkeletonPulse className="h-4 w-24" /></td>
    <td className="px-6 py-4"><SkeletonPulse className="h-6 w-20 rounded-full" /></td>
    <td className="px-6 py-4">
      <div className="flex items-center gap-3">
        <SkeletonPulse className="w-24 h-2" />
        <SkeletonPulse className="h-4 w-10" />
      </div>
    </td>
    <td className="px-6 py-4"><SkeletonPulse className="h-4 w-20" /></td>
    <td className="px-6 py-4"><SkeletonPulse className="h-4 w-16" /></td>
  </tr>
);

const DashboardSkeleton = () => (
  <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
    <Header />
    <div className="pt-24 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <SkeletonPulse className="h-8 w-64 mb-3" />
            <SkeletonPulse className="h-4 w-48" />
          </div>
          <SkeletonPulse className="h-10 w-24" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>

        {/* Table Skeleton */}
        <div 
          className="rounded-2xl overflow-hidden"
          style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderWarm}` }}
        >
          <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.borderWarm}` }}>
            <SkeletonPulse className="h-6 w-40" />
            <SkeletonPulse className="h-4 w-20" />
          </div>
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderWarm}` }}>
                {['Project', 'Partner', 'Status', 'Progress', 'Budget', 'Action'].map((h) => (
                  <th key={h} className="px-6 py-4 text-left">
                    <SkeletonPulse className="h-3 w-16" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonTableRow key={i} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

const SaaSSponsorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');
    if (!token || !userData) { navigate('/xchange/login'); return; }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    await fetchDashboardData(token);
  };

  const fetchDashboardData = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const engRes = await fetch(`${backendUrl}/api/saas/engagements/my`, { headers: { 'Authorization': `Bearer ${token}` } });
      const engData = await engRes.json();
      setEngagements(Array.isArray(engData) ? engData : []);
      setStats({
        activeProjects: engData.filter(e => e.status === 'active').length,
        totalInvested: engData.reduce((sum, e) => sum + (e.totalBudget || 0), 0),
        beneficiariesReached: engData.reduce((sum, e) => sum + (e.beneficiariesReached || 0), 0),
        avgProgress: engData.length > 0 ? Math.round(engData.reduce((sum, e) => sum + (e.progress || 0), 0) / engData.length) : 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const getHealthColor = (health) => {
    switch (health) {
      case 'on_track': return colors.primaryTeal;
      case 'at_risk': return '#F59E0B';
      case 'off_track': return '#EF4444';
      default: return colors.textSecondary;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('xchange_token');
    localStorage.removeItem('xchange_user');
    navigate('/xchange/login');
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const statCards = [
    { icon: Activity, label: 'Active Projects', value: stats?.activeProjects || 0 },
    { icon: DollarSign, label: 'Total Invested', value: formatCurrency(stats?.totalInvested || 0) },
    { icon: Target, label: 'Beneficiaries', value: (stats?.beneficiariesReached || 0).toLocaleString() },
    { icon: TrendingUp, label: 'Avg Progress', value: `${stats?.avgProgress || 0}%` }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
      <Header />
      
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: colors.textPrimary }}>
                Welcome back, {user?.orgName || 'Sponsor'}
              </h1>
              <p className="mt-1" style={{ color: colors.textSecondary }}>
                Manage your CSR investments and track impact
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
              style={{ backgroundColor: colors.bgSage, color: colors.textPrimary, border: `1px solid ${colors.borderSage}` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryTeal; e.currentTarget.style.color = colors.textOnDark; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.bgSage; e.currentTarget.style.color = colors.textPrimary; }}
            >
              Sign Out
            </button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
            {statCards.map((stat, idx) => (
              <div 
                key={idx}
                className="rounded-2xl p-6 transition-all duration-300"
                style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderWarm}` }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = colors.primaryTeal;
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(61, 107, 103, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = colors.borderWarm;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: colors.bgSage }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: colors.primaryTeal }} />
                </div>
                <p className="text-2xl md:text-3xl font-bold" style={{ color: colors.textPrimary }}>{stat.value}</p>
                <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Engagements Table */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderWarm}` }}>
            <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.borderWarm}` }}>
              <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>Active Engagements</h2>
              <button 
                className="text-sm font-medium flex items-center gap-1 transition-colors"
                style={{ color: colors.primaryTeal }}
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {engagements.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: colors.textTertiary }} />
                <p className="font-medium mb-2" style={{ color: colors.textPrimary }}>No active engagements</p>
                <p className="text-sm" style={{ color: colors.textSecondary }}>Start by exploring verified NGOs</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.borderWarm}` }}>
                      {['Project', 'Partner', 'Status', 'Progress', 'Budget', 'Action'].map((header) => (
                        <th key={header} className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textTertiary }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {engagements.slice(0, 5).map((eng, idx) => (
                      <tr 
                        key={eng._id || idx} 
                        className="transition-colors"
                        style={{ borderBottom: idx < engagements.length - 1 ? `1px solid ${colors.borderWarm}` : 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgCream}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium" style={{ color: colors.textPrimary }}>{eng.projectName || 'Unnamed Project'}</p>
                          <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>{eng.sector || 'General'}</p>
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: colors.textPrimary }}>{eng.agencyName || '-'}</td>
                        <td className="px-6 py-4">
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: `${getHealthColor(eng.health)}15`,
                              color: getHealthColor(eng.health)
                            }}
                          >
                            {eng.health?.replace('_', ' ') || 'pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.bgSage }}>
                              <div 
                                className="h-full rounded-full transition-all"
                                style={{ width: `${eng.progress || 0}%`, backgroundColor: colors.primaryTeal }}
                              />
                            </div>
                            <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>{eng.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium" style={{ color: colors.textPrimary }}>
                          {formatCurrency(eng.totalBudget || 0)}
                        </td>
                        <td className="px-6 py-4">
                          <button 
                            className="text-sm font-medium transition-colors"
                            style={{ color: colors.primaryTeal }}
                            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryBrown}
                            onMouseLeave={(e) => e.currentTarget.style.color = colors.primaryTeal}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaaSSponsorDashboard;
